import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import HomeHeader from "../common-components/HomeHeader";
import HomeFooter from "../common-components/HomeFooter";
import DataFetchStatus from "../common/DataFetchStatus";
import NoPurchaseBanner from "../common-components/NoPurchaseBanner";
import { getCmsList } from "../actions/cms";
import { findCmsEntryBySlug } from "../../utility/cmsContent";
import { isDTS } from "../../utility/platform";
import { ROUTES } from "../../constants/routes";
import { getDefaultInfoPage } from "./InfoPageDefaults";
import AmoePolicyContent from "./Amoe/AmoePolicyContent";
import "./infoPage.css";

const safeParse = (value) => parse(DOMPurify.sanitize(value || ""));

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function InfoPage() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const [cmsList, setCmsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const { status, result, message } = await getCmsList(dispatch);
        if (cancelled) return;

        if (status === true && Array.isArray(result)) {
          setCmsList(result);
        } else {
          setCmsList([]);
          setFetchError(message || "Unable to load page content.");
        }
      } catch (err) {
        if (!cancelled) {
          setCmsList([]);
          setFetchError("Unable to load page content. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, dispatch]);

  const cmsEntry = useMemo(
    () => findCmsEntryBySlug(cmsList, slug),
    [cmsList, slug]
  );

  // When the CMS has no entry (or a blank one) for this slug, fall back to a
  // hardcoded default page rather than showing "not published yet" — this
  // matters most for the DTS legal slugs (terms/official-rules/privacy/amoe),
  // which must never be blank per sweepstakes law, and are populated with
  // placeholder content until legal counsel provides final copy via CMS.
  const defaultPage = getDefaultInfoPage(slug);
  const pageTitle = cmsEntry?.title || defaultPage?.title || "Information";
  const hasCmsContent = Boolean(stripHtml(cmsEntry?.content));
  const showDefaultContent = !loading && !fetchError && !hasCmsContent && Boolean(defaultPage);
  const showNotFound = !loading && !fetchError && !cmsEntry && !defaultPage;
  const showEmptyContent = !loading && !fetchError && cmsEntry && !hasCmsContent && !defaultPage;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // The `--dts` modifier switches on the colour rules at the foot of
  // infoPage.css. They exist because index.css's `* { color: ... !important }`
  // flattens this file's whole type hierarchy to white; scoping them here
  // keeps PPH's legal pages rendering exactly as they do today.
  return (
    <section className={`info-page${isDTS() ? " info-page--dts" : ""}`}>
      <HomeHeader />
      <div className="info-page__main hm-page-offset">
        <div className="info-page__hero">
          <Container className="info-page__container">
            <p className="info-page__breadcrumb mb-3">
              <Link to="/">Home</Link>
              <span aria-hidden="true"> / </span>
              <Link to={ROUTES.straightBet}>{isDTS() ? "Play" : "Betting"}</Link>
              <span aria-hidden="true"> / </span>
              <span>{pageTitle}</span>
            </p>
            <h1 className="info-page__hero-title">{pageTitle}</h1>
          </Container>
        </div>

        <Container className="info-page__container info-page__body py-4 pb-5">
          <DataFetchStatus loading={loading} error={fetchError}>
            {showNotFound ? (
              <div className="info-page__empty text-center py-5">
                <div className="info-page__empty-card mx-auto">
                  <h2 className="fs-4 mb-3">Page not found</h2>
                  <p className="mb-4">
                    This page is not set up yet. Browse live lines or contact
                    support for help.
                  </p>
                  <Link to={ROUTES.straightBet} className="btn btn-primary">
                    {isDTS() ? "Go to play" : "Go to betting"}
                  </Link>
                </div>
              </div>
            ) : null}

            {showEmptyContent ? (
              <div className="info-page__empty text-center py-5">
                <div className="info-page__empty-card mx-auto">
                  <h2 className="fs-4 mb-3">{pageTitle}</h2>
                  <p className="mb-4">
                    Content for this page has not been published yet. Please
                    check back soon.
                  </p>
                  <Link to={ROUTES.straightBet} className="btn btn-primary">
                    {isDTS() ? "Go to play" : "Go to betting"}
                  </Link>
                </div>
              </div>
            ) : null}

            {showDefaultContent ? (
              <article className="info-page__article">
                <div className="info-page__content">
                  <defaultPage.Content />
                </div>
              </article>
            ) : null}

            {!loading && hasCmsContent ? (
              <article className="info-page__article">
                {isDTS() && (slug === "terms" || slug === "official-rules") && <NoPurchaseBanner variant="inline" />}
                <div className="info-page__content">
                  {safeParse(cmsEntry.content)}
                </div>
                <div className="info-page__cta">
                  <p className="mb-3">
                    {isDTS() ? "Ready to play?" : "Ready to place a bet?"}
                  </p>
                  <Link to={ROUTES.straightBet} className="btn btn-primary">
                    {isDTS() ? "Start playing" : "Open sportsbook"}
                  </Link>
                </div>
              </article>
            ) : null}

          </DataFetchStatus>
          {!loading && isDTS() && (slug === "amoe" || slug === "official-rules") ? (
            <article className="info-page__article">
              <div className="info-page__content"><AmoePolicyContent /></div>
            </article>
          ) : null}
        </Container>
      </div>
      <HomeFooter />
    </section>
  );
}
